import type { AxiosError } from "axios";
import { cloneDeep } from "lodash-es";
import { computed, reactive, toRefs } from "vue";

import { REPLICATION_DELAY } from "@/common/api/utils";
import VocabService from "@/common/api/vocab.service";
import { sleep } from "@/common/utils";
import router from "@/router";

export type Question = {
  id: number;
  lemma: string;
  lemmaWordclass: string;
  translation: string;
  translationWordclass: string;
  options: string[];
  option: string;
  type: QuestionTypeEnum;
  answerSummaries: Record<string, number>;
};

type UserAnswer = {
  quizQuestionId: number;
  isCorrect: boolean;
};

type Round = {
  id: number;
  questions: Question[];
  userAnswers: UserAnswer[];
  questionType: QuestionTypeEnum;
};

export enum GameTypeEnum {
  TrueFalse = "tf",
  MultipleChoice = "mc",
  Writing = "wr",
  MatchingPairs = "mp",
  DragDrop = "dd",
  EasyMix = "em",
  HardMix = "hm"
}

export enum QuestionTypeEnum {
  TrueFalse = GameTypeEnum.TrueFalse,
  MultipleChoice = GameTypeEnum.MultipleChoice,
  Writing = GameTypeEnum.Writing,
  MatchingPairs = GameTypeEnum.MatchingPairs,
  DragDrop = GameTypeEnum.DragDrop
}

export enum QuizStateEnum {
  NotStarted = 0,
  Prompted = 1,
  Answered = 2,
  Checkpoint = 3,
  Finished = 4,
  AchievementProgress = 5
}

export enum AnswerStateEnum {
  Initial = 0,
  Correct = 1,
  Incorrect = 2,
  Pending = 3,
  Hidden = 4
}

interface State {
  quizState: QuizStateEnum;
  rounds: Round[];
  checkpoints: number[];
  currentRoundIndex?: number;
  wrongAnswers: Question[];
}

const initialState: State = {
  quizState: QuizStateEnum.NotStarted,
  rounds: [],
  checkpoints: [],
  currentRoundIndex: undefined,
  wrongAnswers: []
};

export const useVocabQuiz = () => {
  const state = reactive<State>(cloneDeep(initialState));

  const shareCode = computed<string>(() => router.currentRoute.value.params.shareCode as string);
  const quizCode = computed<string>(() => router.currentRoute.value.params.quizCode as string);

  const roundsCount = computed<number>(() => state.rounds.length);
  const currentRound = computed<Round | undefined>(() => {
    if (state.currentRoundIndex === undefined) {
      return;
    }

    return state.rounds[state.currentRoundIndex];
  });

  const shouldFinishQuiz = computed<boolean>(() => {
    if (state.currentRoundIndex === undefined) {
      return false;
    }

    return state.currentRoundIndex + 1 > roundsCount.value;
  });

  const initializeQuiz = async (): Promise<void> => {
    try {
      const { rounds, checkpoints, currentRoundIndex } = (
        await VocabService.getQuizRounds(quizCode.value)
      ).data.data;

      Object.assign(state, <State>{
        quizState: QuizStateEnum.NotStarted,
        rounds,
        checkpoints,
        // NULL index from backend means no more rounds = completed all
        currentRoundIndex: currentRoundIndex === null ? rounds.length : currentRoundIndex
      });

      if (shouldFinishQuiz.value) {
        return finishQuiz();
      }
    } catch (e: unknown) {
      const error = e as AxiosError;
      if (error.response?.status === 404) {
        router.push({ name: "vocab.list", params: { shareCode: shareCode.value } });
      }

      throw error;
    }
  };

  const finishQuiz = async (): Promise<void> => {
    state.quizState = QuizStateEnum.Finished;

    await sleep(REPLICATION_DELAY);

    const response = await VocabService.getQuizQuestions(quizCode.value, {
      limit: 3,
      answerFilter: "incorrect"
    });

    state.wrongAnswers = response.data.data.questions;
  };

  const validateAnswer = async (questionId: number, answer: string): Promise<AnswerStateEnum> => {
    state.quizState = QuizStateEnum.Answered;

    const isCorrect = (await VocabService.createQuizAnswer(quizCode.value, questionId, answer)).data
      .data;

    return isCorrect ? AnswerStateEnum.Correct : AnswerStateEnum.Incorrect;
  };

  const enableQuiz = (): void => {
    if (shouldFinishQuiz.value) {
      finishQuiz();
      return;
    }

    state.quizState = QuizStateEnum.Prompted;
  };

  const isCheckpoint = (index: number): boolean => state.checkpoints.includes(index);

  const nextRound = async (delay = 0): Promise<void> => {
    if (state.currentRoundIndex === undefined) {
      return;
    }

    if (delay) {
      await sleep(delay);
    }

    if (isCheckpoint(state.currentRoundIndex)) {
      state.quizState = QuizStateEnum.Checkpoint;
    } else {
      state.quizState = QuizStateEnum.Prompted;
    }

    state.currentRoundIndex++;

    if (shouldFinishQuiz.value) {
      finishQuiz();
    }
  };

  const skipRound = async (): Promise<void> => {
    if (!currentRound.value || state.quizState === QuizStateEnum.Answered) {
      return;
    }

    state.quizState = QuizStateEnum.Answered;

    // No need to await the response coming back, we assume its a'ok.
    VocabService.skipQuizRound(quizCode.value, currentRound.value.id);

    await nextRound();
  };

  const isFirstRound = (): boolean => state.currentRoundIndex === 0;

  return {
    ...toRefs(state),

    currentRound,

    initializeQuiz,
    isFirstRound,
    validateAnswer,
    enableQuiz,
    nextRound,
    skipRound
  };
};
